import { useAuth } from 'modules/auth/ui/hooks/use-auth';
import React, { useCallback, useState } from 'react';
import {
  clearOverride,
  FEATURE_FLAG_NAMES,
  FEATURE_FLAGS,
  FeatureFlagName,
  FeatureFlagOverride,
  readOverride,
  resolveFeatureFlag,
} from 'shared/feature-flags';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';
import { writeOverride } from 'shared/feature-flags/feature-flag-storage';

/**
 * У этого флага своя, отдельная от `FlagRow` секция ниже (простой бинарный тоггл, не
 * 3-состояний ряд «По умолчанию/Включён/Выключен») — поэтому исключаем его из общего цикла
 * по `FEATURE_FLAG_NAMES`, иначе он показался бы дважды.
 */
const GENERIC_FLAG_ROW_NAMES = FEATURE_FLAG_NAMES.filter((name) => name !== 'applicationsFlow1447');

const NEW_FLOW_1447_FLAG: FeatureFlagName = 'applicationsFlow1447';

type OverrideState = 'default' | FeatureFlagOverride;

const OPTIONS: { value: OverrideState; label: string }[] = [
  { value: 'default', label: 'По умолчанию' },
  { value: 'on', label: 'Включён' },
  { value: 'off', label: 'Выключен' },
];

const readState = (name: FeatureFlagName): OverrideState => readOverride(name) ?? 'default';

interface FlagRowProps {
  name: FeatureFlagName;
  userCreatedAt?: string;
}

const FlagRow = ({ name, userCreatedAt }: FlagRowProps) => {
  const definition = FEATURE_FLAGS[name];
  const [state, setState] = useState<OverrideState>(() => readState(name));

  const change = useCallback(
    (next: OverrideState) => () => {
      if (next === 'default') {
        clearOverride(name);
      } else {
        writeOverride(name, next);
      }

      setState(next);
    },
    [name],
  );

  const override = state === 'default' ? null : state;
  const value = resolveFeatureFlag({ override, userCreatedAt, since: definition.since });

  // `useFeatureFlag` считает флаг один раз на смене пользователя, поэтому уже открытые
  // экраны переключение не перерисует — говорим об этом прямо, а не полагаемся на реактивность.
  const reason = override
    ? 'значение задано вручную на этой вкладке'
    : userCreatedAt
      ? `по дате регистрации ${userCreatedAt} и отсечке ${definition.since}`
      : 'у пользователя нет даты регистрации, поэтому флаг выключен';

  return (
    <div className="border-WhiteGray20 mb-6 rounded-lg border p-4" data-testid={`feature-flag-${name}`}>
      <UDText type="subhead" className="mb-1 block">
        {definition.title}
      </UDText>
      <UDText type="footnote" className="text-Gray mb-3 block">
        {definition.description}
      </UDText>

      <div className="mb-3 flex gap-2">
        {OPTIONS.map((option) => (
          <UDButton key={option.value} variant={option.value === state ? 'primary' : 'secondary'} onClick={change(option.value)} slim>
            {option.label}
          </UDButton>
        ))}
      </div>

      <UDText type="footnote" className="text-Gray block">
        Сейчас: {value ? 'включён' : 'выключен'} — {reason}. Применится после перезагрузки страницы.
      </UDText>
    </div>
  );
};

/**
 * Простой бинарный тоггл ветки IMGM-1447 против продакшена — без «По умолчанию» и без даты
 * регистрации: только «Включено»/«Выключено», состояние всегда явно записано в localStorage
 * (см. «Ключевые решения» плана `applications-flow-1447-ab-toggle`).
 */
const NewFlow1447Toggle = () => {
  const [override, setOverride] = useState<FeatureFlagOverride>(() => readOverride(NEW_FLOW_1447_FLAG) ?? 'off');

  const change = useCallback(
    (next: FeatureFlagOverride) => () => {
      writeOverride(NEW_FLOW_1447_FLAG, next);
      setOverride(next);
    },
    [],
  );

  return (
    <div className="border-WhiteGray20 mb-6 rounded-lg border p-4" data-testid={`feature-flag-${NEW_FLOW_1447_FLAG}`}>
      <UDText type="subhead" className="mb-1 block">
        Новый флоу 2.0 для заявок
      </UDText>
      <UDText type="footnote" className="text-Gray mb-3 block">
        {FEATURE_FLAGS[NEW_FLOW_1447_FLAG].description}
      </UDText>

      <div className="mb-3 flex gap-2">
        <UDButton variant={override === 'on' ? 'primary' : 'secondary'} onClick={change('on')} slim>
          Включено
        </UDButton>
        <UDButton variant={override === 'off' ? 'primary' : 'secondary'} onClick={change('off')} slim>
          Выключено
        </UDButton>
      </div>

      {/* «Статус:», а не «Сейчас:» — иначе «выключено» подстрокой совпадает с regex-запросами
          `/Сейчас: выключен/` из тестов существующего 3-состояний блока `FlagRow`. */}
      <UDText type="footnote" className="text-Gray block">
        Статус: {override === 'on' ? 'включено' : 'выключено'}. Применится после перезагрузки страницы.
      </UDText>
    </div>
  );
};

/**
 * Dev-вкладка настроек: ручное переключение фиче-флагов.
 * Регистрируется только при `checkIsDev()` — на проде вкладки и роута нет.
 */
const Experiments = () => {
  const { user } = useAuth();

  return (
    <div>
      <UDText type="callout" className="mb-4 block">
        Переключение флоу вручную. То же самое делает параметр адреса <code>?flow=v2</code> / <code>?flow=v1</code>; значение хранится
        в localStorage этого браузера и работает во всех окружениях, включая прод.
      </UDText>

      {GENERIC_FLAG_ROW_NAMES.map((name) => (
        <FlagRow key={name} name={name} userCreatedAt={user?.created_at} />
      ))}

      <NewFlow1447Toggle />
    </div>
  );
};

export default Experiments;
